<script setup lang="ts">
import { computed, ref } from 'vue';
import { useAudioPlayer } from '@/composables/useAudioPlayer';

interface Props {
  direction?: 'horizontal' | 'vertical';
  popupMode?: boolean;
  showSlider?: boolean;
  showLabel?: boolean;
  sliderWidth?: string;
  sliderHeight?: string;
}

const props = withDefaults(defineProps<Props>(), {
  direction: 'horizontal',
  popupMode: false,
  showSlider: true,
  showLabel: false,
  sliderWidth: '80px',
  sliderHeight: '120px',
});

const emit = defineEmits<{
  (e: 'update:volume', value: number): void;
  (e: 'toggle-mute'): void;
}>();

const audioPlayer = useAudioPlayer();
const menuOpen = ref(false);

const volume = computed(() => audioPlayer.volume.value);
const isMuted = computed(() => audioPlayer.isMuted.value);

const volumeIcon = computed(() => {
  if (isMuted.value || volume.value === 0) return 'mdi-volume-off';
  if (volume.value < 33) return 'mdi-volume-low';
  if (volume.value < 66) return 'mdi-volume-medium';
  return 'mdi-volume-high';
});

function handleToggleMute() {
  audioPlayer.toggleMute();
  emit('toggle-mute');
}

function handleVolumeChange(value: number | number[]) {
  const newVolume = Array.isArray(value) ? value[0] : value;
  audioPlayer.setVolume(newVolume);
  emit('update:volume', newVolume);
}
</script>

<template>
  <div class="volume-control" :class="`volume-control--${direction}`">
    <!-- Popup Mode (for full-screen player) -->
    <template v-if="popupMode">
      <v-menu
        v-model="menuOpen"
        :close-on-content-click="false"
        location="top"
        offset="12"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            icon
            variant="text"
            size="large"
            :class="{ active: menuOpen }"
            v-bind="menuProps"
          >
            <v-icon size="28" color="white">{{ volumeIcon }}</v-icon>
          </v-btn>
        </template>

        <v-card class="volume-control__popup" elevation="8">
          <div class="volume-control__popup-content">
            <v-slider
              :model-value="volume"
              :max="100"
              :step="1"
              direction="vertical"
              hide-details
              color="primary"
              track-color="grey-darken-2"
              thumb-color="primary"
              class="volume-control__vertical-slider"
              @update:model-value="handleVolumeChange"
            />
            <div v-if="showLabel" class="volume-control__label">
              {{ Math.round(volume) }}%
            </div>
          </div>
        </v-card>
      </v-menu>
    </template>

    <!-- Inline Mode (for desktop bar) -->
    <template v-else>
      <v-btn icon variant="text" size="small" @click="handleToggleMute">
        <v-icon>{{ volumeIcon }}</v-icon>
      </v-btn>

      <v-slider
        v-if="showSlider"
        :model-value="volume"
        :max="100"
        hide-details
        density="compact"
        color="primary"
        class="volume-control__slider"
        :style="{ width: direction === 'horizontal' ? sliderWidth : undefined }"
        :direction="direction"
        @update:model-value="handleVolumeChange"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.volume-control {
  display: flex;
  align-items: center;

  &--horizontal {
    flex-direction: row;
    gap: 4px;
  }

  &--vertical {
    flex-direction: column;
    gap: 8px;
  }

  &__slider {
    min-width: 60px;
  }

  &__popup {
    background: rgb(var(--v-theme-surface)) !important;
    border-radius: 12px !important;
    padding: 16px 12px;
  }

  &__popup-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    height: 180px;
    overflow: hidden;
  }

  &__vertical-slider {
    height: 120px;

    :deep(.v-slider-track__fill),
    :deep(.v-slider-track__background) {
      width: 4px;
    }

    :deep(.v-slider-thumb) {
      width: 16px;
      height: 16px;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    text-align: center;
    min-width: 40px;
  }

  // Active state for popup button
  .v-btn.active {
    background: rgb(var(--v-theme-primary)) !important;

    :deep(.v-icon) {
      color: white !important;
    }
  }
}
</style>
